<template>
  <div v-if="!testData || !contactData" class="flex justify-center items-center py-10">
    <d-loader />
  </div>
  <div v-else>
    <div v-if="contactIsExpired" class="py-10 flex justify-center">
      <div class="w-full md:w-2/3">
        <p class="text-3xl mb-3  text-center">
          Unable to Proceed
        </p>
        <p class="text-center">
          Based on your current QCP status, you appear to be ineligible to take this test. Please contact the AWI Quality Certification Corporation to discuss your participant status and resolve the matter.
        </p>
        <div class="flex justify-center pt-10">
          <d-btn href="/">
            Go Back Home
          </d-btn>
        </div>
      </div>
    </div>
    <div v-else class="flex flex-wrap -mx-5 justify-center">
      <div class="w-full md:w-3/12">
        <p class="text-2xl mb-3">
          {{ computedTestName }}
        </p>
        <p class="mb-3">
          <strong>Started:</strong><br>
          {{ testData.startDatetime | formatShortDate }}
        </p>
        <p class="mb-3">
          <strong>Last Day to Complete:</strong><br>
          {{ testData.mustFinishByDateTime | formatShortDate }}
        </p>
        <div class="w-3/4">
          <hr>
        </div>
        <test-info-modals />
      </div>
      <transition name="fade" mode="out-in">
        <div v-if="!testData.completed" class="w-full md:w-7/12">
          <transition name="fade" mode="out-in">
            <router-view :key="this.$route.params.questionNum" />
          </transition>
        </div>
        <div v-else class="w-full md:w-7/12 text-center">
          <p class="text-2xl">
            This test has been completed
          </p>
          <div>
            <p v-if="testData.passed" class="font-bold text-5xl">
              You Passed!
            </p>
            <p v-else class="font-bold text-5xl">
              You did not pass
            </p>
          </div>
          <p>You earned a {{ (testData.finalPercentage * 100).toFixed(0) }}%</p>
          <div class="pt-16 flex justify-center">
            <d-button href="/">
              Go Back to Your Account Page
            </d-button>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
  import axios from 'axios';
  import dayjs from 'dayjs';
  import { createNamespacedHelpers } from 'vuex';
  import { TestNames } from '@resources/scripts/models/enums';
  import { queryFirstDrafter } from '@resources/scripts/services/api/filemakerDrafters.api';
  import { queryFirstContact } from '@resources/scripts/services/api/filemakerContacts.api';
  import TestInfoModals from './TestInfoModals.vue';
  const { mapGetters, mapActions } = createNamespacedHelpers('tests');

  export default {
    components: {
      TestInfoModals,
    },

    data() {
      return {
        TestNames,
      };
    },

    computed: {
      ...mapGetters([
        'testData',
        'contactData',
        'firstQuestionAnswered',
      ]),

      computedTestName() {
        switch (this.testData.testType) {
          case 'Policies':
            return TestNames.Policies;
          case 'Standards':
            return TestNames.Standards;
          case 'Drafting':
            return TestNames.Drafting;
          default:
            return '';
        }
      },

      contactIsExpired() {
        if (this.$d2Auth.hasRole('Drafter')) {
          return this.contactData ? dayjs(this.contactData.Expiration_Date).add(1, 'days').isBefore(dayjs()) : false;
        }
        return this.contactData ? dayjs(this.contactData.expiration_date).add(1, 'days').isBefore(dayjs()) : false;
      },
    },

    async mounted() {
      await this.fetchTestData();
      this.fetchFilemakerTestContact();
    },

    methods: {
      ...mapActions([
        'updateTestData',
        'updateQuestions',
        'updateTestContactData',
      ]),

      async fetchTestData() {
        const { data } = await axios.get(`/api/tests/${this.$route.params.id}`);

        this.updateTestData(data);
        this.updateQuestions(data.questions);
        if (!this.$route.params.questionNum && this.$route.name !== 'summmary' && this.$route.name !== 'intro') {
          if (this.testData.currentQuestionNumber === 1 && !this.firstQuestionAnswered) {
            this.$router.replace({ name: 'intro' });
            return;
          }
          if (this.testData.currentQuestionNumber > this.testData.questions.length) {
            this.$router.replace({ name: 'summary' });
          } else {
            this.$router.replace({ name: 'question', params: { questionNum: this.testData.currentQuestionNumber } });
          }
        }
      },

      async fetchFilemakerTestContact() {
        if (this.$d2Auth.hasRole('Drafter')) {
          const { data } = await queryFirstDrafter({
            'PDA Contact ID': this.$d2Auth.profile.username,
          });
          this.updateTestContactData(data);
        } else {
          const { data: contactQuery } = await queryFirstContact({
            'Contact ID': this.$d2Auth.profile.username,
          });
          const { data } = await axios.get(`/api/test-organization/${contactQuery['Organization ID']}`);
          data.contact_is_liaison = contactQuery['QCP liaison'];
          this.updateTestContactData(data);
        }
      },
    },
  };
</script>